import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { post } from '../../../utils/apiClient';
import { consumeDemoBypass } from '../../../app/router/helpers';
import { routeForRole } from '../constants';
import { useTabContextSafe } from '../../../contexts/TabContext';
import type { AuthUser } from '../../../contexts/AuthContext';
import { formatProviderFullName } from '../../../utils/providerDisplayName';

interface CharacterModeBannerProps {
  user: AuthUser;
}

export default function CharacterModeBanner({ user }: CharacterModeBannerProps) {
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const tabContext = useTabContextSafe();

  if (!user.impersonates_user_id) return null;

  const characterName = formatProviderFullName(user) || 'demo character';
  // Shared-demo sessions use the banner CTA to start clinic setup; claimed
  // training sessions use it to return to the real clinic account.
  const isSharedDemo = !!user.demo_mode && !user.claimed_org;
  const showReturn = !!user.claimed_org;
  const returnLabel = `Return to ${user.clinic_name || 'your clinic'}`;

  const returnToSelf = async () => {
    setSwitching(true);
    setSwitchError(null);
    try {
      const response = await post('/api/auth/switch-character', { user_id: 'self' });
      if (!response.ok) {
        setSwitchError('Could not return to your account. Please try again.');
        return;
      }

      tabContext?.clearSavedTabs?.();
      consumeDemoBypass();
      const result = await response.json();
      window.location.href = routeForRole(result.role || '');
    } catch {
      setSwitchError('Could not return to your account. Please try again.');
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="shrink-0 border-b-4 border-orange-500 bg-orange-50 px-2 py-2 sm:px-3 text-orange-950 dark:bg-orange-950 dark:text-orange-50">
      <div className="mx-auto flex max-w-screen-xl flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:flex-nowrap sm:gap-x-3 sm:text-sm">
        <span className="min-w-0 flex-1 truncate">
          Viewing as:{' '}
          <Link to="/characters" className="font-bold underline decoration-dotted underline-offset-2" title="Switch character">
            {characterName}
          </Link>
        </span>
        {(showReturn || isSharedDemo || switchError) && (
          <div className="flex min-w-0 shrink-0 items-center gap-1.5">
            {switchError && <span role="alert">{switchError}</span>}
            {isSharedDemo && (
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event('open-claim-dialog'))}
                className="rounded border border-orange-700 px-1.5 py-1 font-medium leading-snug whitespace-nowrap text-center hover:bg-orange-100 dark:border-orange-300 dark:hover:bg-orange-900"
              >
                Name your clinic
              </button>
            )}
            {showReturn && (
              <button
                type="button"
                onClick={returnToSelf}
                disabled={switching}
                className="rounded border border-orange-700 px-1.5 py-1 sm:px-3 sm:py-1.5 font-medium leading-snug whitespace-nowrap text-center hover:bg-orange-100 disabled:cursor-wait disabled:opacity-60 dark:border-orange-300 dark:hover:bg-orange-900"
              >
                {switching ? 'Returning…' : returnLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
